import type { IControlVariantTemplate } from './useCreateCampaignVariant';
import type { Campaign } from '~/modules/campaign/types';

import { CAMPAIGN_PRESET, CAMPAIGN_TYPE, VARIANT_TEMPLATE_PRESET } from '~/constants/campaign';
import { useTrackingAnalytic } from '~/modules/tracking/hooks/useTrackingAnalytic';
import { useCreateCampaignVariant } from './useCreateCampaignVariant';
import { useCampaignNavigate } from './useCampaignNavigate';
import { useCreateCampaign } from './useCreateCampaign';
import { useCampaignStore } from '~/modules/campaign';
import { EVENT_ANALYTIC } from '~/modules/tracking';
import { generateNameWithTime } from '../utils';
import { useTranslation } from 'react-i18next';
import { useToast } from '~/hooks/useToast';
import { CAMPAIGN_MSG } from '../constants';
import { useState } from 'react';

export interface ICreateCampaignDraftProps {
  isRedirect?: boolean;
  controlTemplate?: IControlVariantTemplate;
}

export interface ICreateCampaignDraft<T extends Campaign> {
  isLoadingCreate: boolean;
  onCreateCampaignDraft: (props?: ICreateCampaignDraftProps) => Promise<T>;
}

export const useCreateCampaignDraft = <T extends Campaign>(): ICreateCampaignDraft<T> => {
  const [isLoadingCreate, setIsLoadingCreate] = useState(false);

  const { t } = useTranslation();
  const { onCreateCampaign } = useCreateCampaign();
  const { createCampaignVariant } = useCreateCampaignVariant({ template: VARIANT_TEMPLATE_PRESET });
  const { onEditCampaign } = useCampaignNavigate();
  const { shopifyToast } = useToast();
  const { trackEvent } = useTrackingAnalytic();

  const upsertCampaign = useCampaignStore((state) => state.upsertCampaign);

  const onCreateCampaignDraft = async (props?: ICreateCampaignDraftProps) => {
    const { isRedirect = true, controlTemplate } = props ?? {};

    try {
      setIsLoadingCreate(true);

      const campaignCreate = CAMPAIGN_PRESET;
      campaignCreate.type = CAMPAIGN_TYPE.TEMPLATE;
      campaignCreate.name = generateNameWithTime(t('Template experiment'));
      const campaign = await onCreateCampaign({ input: campaignCreate });
      if (!campaign) throw new Error(CAMPAIGN_MSG.CREATE.FAILED);

      const updatedCampaign = await createCampaignVariant(campaign, { controlTemplate });

      upsertCampaign(updatedCampaign);
      isRedirect && onEditCampaign(campaign);

      trackEvent(EVENT_ANALYTIC.GEMX_CAMPAIGN_CREATE, {
        campaign: {
          ...campaign,
        },
      });

      return updatedCampaign as T;
    } catch (error) {
      shopifyToast(CAMPAIGN_MSG.CREATE.FAILED, { isError: true });
      throw new Error((error as Error).message, { cause: error });
    } finally {
      setIsLoadingCreate(false);
    }
  };

  return {
    isLoadingCreate,
    onCreateCampaignDraft,
  };
};
